import React, { useState, useEffect, useRef } from 'react';
import { Card, Button, Alert, Spinner, Modal, Form } from 'react-bootstrap';
import {
  getIntegrationsHubspot,
  postIntegrationsHubspot,
  deleteIntegrationsHubspotIntegrationId,
  postIntegrationsHubspotIntegrationIdTestConnection,
} from '../../api/generated/arrayscapeGamingDashboardAPI';
import { useNotifications } from '../../contexts/NotificationContext';

const HubSpotTab: React.FC = () => {
  const [hubspotIntegrations, setHubspotIntegrations] = useState<any[]>([]);
  const [isLoadingIntegrations, setIsLoadingIntegrations] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [showAddIntegration, setShowAddIntegration] = useState(false);

  // Form fields for OAuth credentials
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [redirectUri, setRedirectUri] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');

  // Get active syncs to monitor completion
  const { activeSyncs } = useNotifications();

  // Ref to track previous sync states
  const previousSyncStatesRef = useRef<Map<string, string>>(new Map());

  // Fetch integrations on mount
  useEffect(() => {
    fetchHubSpotIntegrations();
  }, []);

  // Monitor sync completion and refetch integration data
  useEffect(() => {
    activeSyncs.forEach((syncEvent, syncId) => {
      const prevStatus = previousSyncStatesRef.current.get(syncId);
      const currentStatus = syncEvent.status;

      // Detect completion or failure (transition from started/progress to completed/failed)
      if ((prevStatus === 'started' || prevStatus === 'progress') &&
          (currentStatus === 'completed' || currentStatus === 'failed')) {

        const integrationId = syncEvent.integration_id;

        // Refetch HubSpot integrations if this was a HubSpot sync
        if (hubspotIntegrations.some(int => int.id === integrationId)) {
          console.log(`[HubSpotTab] sync ${currentStatus} for ${integrationId}, refetching integrations`);
          fetchHubSpotIntegrations();
        }
      }

      // Update the tracked state
      previousSyncStatesRef.current.set(syncId, currentStatus);
    });
  }, [activeSyncs, hubspotIntegrations]);

  const fetchHubSpotIntegrations = async () => {
    setIsLoadingIntegrations(true);
    setError(null);
    try {
      const response = await getIntegrationsHubspot();
      setHubspotIntegrations(response.integrations || []);
    } catch (error) {
      console.error('Error fetching HubSpot integrations:', error);
      setError('Failed to fetch HubSpot integrations');
    } finally {
      setIsLoadingIntegrations(false);
    }
  };

  const handleHubSpotConnect = async () => {
    // Validate form fields
    if (!clientId.trim()) {
      setError('Client ID is required');
      return;
    }
    if (!clientSecret.trim()) {
      setError('Client Secret is required');
      return;
    }
    if (!redirectUri.trim()) {
      setError('Redirect URI is required');
      return;
    }
    if (!webhookSecret.trim()) {
      setError('Webhook Secret is required');
      return;
    }

    setIsConnecting(true);
    setError(null);
    try {
      const response = await postIntegrationsHubspot({
        client_id: clientId.trim(),
        client_secret: clientSecret.trim(),
        redirect_uri: redirectUri.trim(),
        webhook_secret: webhookSecret.trim(),
      });
      // Open HubSpot OAuth URL in a new window
      if (response.auth_url) {
        const authWindow = window.open(response.auth_url, 'HubSpot Auth', 'width=800,height=600');

        // Listen for success message from popup
        const handleMessage = (event: MessageEvent) => {
          if (event.data.type === 'HUBSPOT_AUTH_SUCCESS') {
            window.removeEventListener('message', handleMessage);
            setIsConnecting(false);
            setShowAddIntegration(false);
            // Clear form fields
            setClientId('');
            setClientSecret('');
            setRedirectUri('');
            setWebhookSecret('');
            fetchHubSpotIntegrations();
          }
        };

        window.addEventListener('message', handleMessage);

        // Fallback: Poll for window closure (in case postMessage doesn't work)
        const pollInterval = setInterval(() => {
          if (authWindow?.closed) {
            clearInterval(pollInterval);
            window.removeEventListener('message', handleMessage);
            setIsConnecting(false);
            // Refresh status after window closes
            fetchHubSpotIntegrations();
          }
        }, 1000);

        // Cleanup after 5 minutes (in case window doesn't close)
        setTimeout(() => {
          clearInterval(pollInterval);
          window.removeEventListener('message', handleMessage);
          if (!authWindow?.closed) {
            authWindow?.close();
          }
          setIsConnecting(false);
        }, 300000);
      }
    } catch (error: any) {
      console.error('Error initiating HubSpot connection:', error);
      setError('Failed to initiate HubSpot connection');
      setIsConnecting(false);
    }
  };

  const handleDeleteIntegration = async (integrationId: string, hubName: string) => {
    if (!window.confirm(`Are you sure you want to delete the HubSpot integration "${hubName || integrationId}"?`)) {
      return;
    }

    try {
      await deleteIntegrationsHubspotIntegrationId(integrationId);
      await fetchHubSpotIntegrations();
    } catch (error) {
      console.error('Error deleting HubSpot integration:', error);
      setError('Failed to delete HubSpot integration');
    }
  };

  const handleTestConnection = async (integrationId: string) => {
    try {
      await postIntegrationsHubspotIntegrationIdTestConnection(integrationId);
      await fetchHubSpotIntegrations();
    } catch (error: any) {
      console.error('Error testing HubSpot connection:', error);
      await fetchHubSpotIntegrations();
    }
  };

  return (
    <>
      <Card className="">
        <Card.Body>
          <div className="d-flex align-items-center justify-content-between mb-4">
            <div className="d-flex align-items-center">
              <div className="me-3 bg-header-control rounded p-2">
                <i className="bi bi-people text-warning" style={{ fontSize: '2rem' }}></i>
              </div>
              <div>
                <h5 className="mb-1">HubSpot Integrations</h5>
                <p className="text-muted mb-0">Sync leads, contacts, and deals from HubSpot CRM</p>
              </div>
            </div>
            <Button
              variant="primary"
              onClick={() => setShowAddIntegration(true)}
              disabled={isConnecting}
            >
              <i className="bi bi-plus-circle me-1"></i>
              Add Integration
            </Button>
          </div>

          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {isLoadingIntegrations ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
              <p className="mt-2 text-muted">Loading integrations...</p>
            </div>
          ) : hubspotIntegrations.length === 0 ? (
            <div className="text-center py-5">
              <div className="d-inline-block bg-header-control rounded p-2">
                <i className="bi bi-people text-warning" style={{ fontSize: '3rem' }}></i>
              </div>
              <h6 className="mt-3 text-muted">No HubSpot Integrations</h6>
              <p className="text-muted">Add your first HubSpot integration to get started</p>
            </div>
          ) : (
            <div className="row">
              {hubspotIntegrations.map((integration) => (
                <div key={integration.id} className="col-md-6 mb-3">
                  <Card className="border">
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start mb-3">
                        <div className="flex-grow-1">
                          <h6 className="mb-1">
                            {integration.hub_name || 'HubSpot Account'}
                          </h6>
                          <div className="text-muted small">
                            <i className="bi bi-hash me-1"></i>
                            Hub ID: {integration.hub_id || 'Pending'}
                          </div>
                        </div>
                        <span className={`badge ${
                          integration.status === 'active' ? 'bg-success' :
                          integration.status === 'pending' ? 'bg-warning' : 'bg-secondary'
                        }`}>
                          {integration.status === 'active' ? 'Connected' :
                           integration.status === 'pending' ? 'Pending' : 'Inactive'}
                        </span>
                      </div>

                      <div className="mb-3 pt-2 border-top">
                        <small className="text-muted d-block">Last Sync:</small>
                        <small>
                          {integration.last_sync_at
                            ? new Date(integration.last_sync_at).toLocaleString()
                            : 'Never'
                          }
                        </small>
                      </div>

                      <div className="d-flex gap-2 mb-3">
                        <Button
                          variant="outline-success"
                          size="sm"
                          onClick={() => handleTestConnection(integration.id)}
                          disabled={integration.status !== 'active'}
                        >
                          <i className="bi bi-check-circle me-1"></i>
                          Test Connection
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDeleteIntegration(integration.id, integration.hub_name)}
                        >
                          <i className="bi bi-trash me-1"></i>
                          Delete
                        </Button>
                      </div>

                      {integration.connection_message && (
                        <div className={`alert ${integration.connection_status === 'connected' ? 'alert-success' : 'alert-danger'} alert-sm mb-3`} role="alert">
                          <i className={`bi ${integration.connection_status === 'connected' ? 'bi-check-circle' : 'bi-exclamation-triangle'} me-1`}></i>
                          <small>{integration.connection_message}</small>
                        </div>
                      )}

                      <div className="mt-3 pt-3 border-top">
                        <div className="row">
                          <div className="col">
                            <small className="text-muted">
                              <i className={`bi ${integration.sync_contacts ? 'bi-check-circle-fill text-success' : 'bi-x-circle text-muted'} me-1`}></i>
                              Sync Contacts
                            </small>
                          </div>
                          <div className="col">
                            <small className="text-muted">
                              <i className={`bi ${integration.sync_deals ? 'bi-check-circle-fill text-success' : 'bi-x-circle text-muted'} me-1`}></i>
                              Sync Deals
                            </small>
                          </div>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </div>
              ))}
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Add HubSpot Integration Modal */}
      <Modal show={showAddIntegration} onHide={() => {
        setShowAddIntegration(false);
        setError(null);
        setClientId('');
        setClientSecret('');
        setRedirectUri('');
        setWebhookSecret('');
      }} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add New HubSpot Integration</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          <p className="text-muted mb-3">
            Enter your HubSpot OAuth App credentials. You can find these in your{' '}
            <a href="https://app.hubspot.com/developer" target="_blank" rel="noopener noreferrer">
              HubSpot Developer Account
            </a>{' '}
            under your app settings.
          </p>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Client ID <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter your HubSpot Client ID"
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                disabled={isConnecting}
              />
              <Form.Text className="text-muted">
                Found in your HubSpot app's "Auth" settings
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Client Secret <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="password"
                placeholder="Enter your HubSpot Client Secret"
                value={clientSecret}
                onChange={(e) => setClientSecret(e.target.value)}
                disabled={isConnecting}
              />
              <Form.Text className="text-muted">
                Found in your HubSpot app's "Auth" settings
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Redirect URI <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g., https://yourdomain.com/api/v1/integrations/hubspot/callback"
                value={redirectUri}
                onChange={(e) => setRedirectUri(e.target.value)}
                disabled={isConnecting}
              />
              <Form.Text className="text-muted">
                Must match the redirect URI configured in your HubSpot app
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Webhook Secret <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="password"
                placeholder="Enter your HubSpot Webhook Secret"
                value={webhookSecret}
                onChange={(e) => setWebhookSecret(e.target.value)}
                disabled={isConnecting}
              />
              <Form.Text className="text-muted">
                Found in your HubSpot app's "Webhooks" settings. Used for webhook signature validation.
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => {
              setShowAddIntegration(false);
              setError(null);
              setClientId('');
              setClientSecret('');
              setRedirectUri('');
              setWebhookSecret('');
            }}
            disabled={isConnecting}
          >
            Cancel
          </Button>
          <Button
            variant="warning"
            onClick={handleHubSpotConnect}
            disabled={isConnecting || !clientId.trim() || !clientSecret.trim() || !redirectUri.trim() || !webhookSecret.trim()}
          >
            {isConnecting ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Connecting...
              </>
            ) : (
              <>
                <i className="bi bi-box-arrow-up-right me-1"></i>
                Connect to HubSpot
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default HubSpotTab;
